"use client"

import * as React from "react"

import { cn } from "../../lib/utils"
import { triggerHaptic } from "../../lib/haptics"

/**
 * A toggle, owned end to end.
 *
 * WHY IT IS NOT A LIBRARY WRAPPER ANY MORE. This used to wrap PrimeReact's `InputSwitch` and style it from a
 * companion `switch.css` full of `.ibada-switch.p-inputswitch` overrides. That worked only as long as
 * PrimeReact's own theme was absent — and the admin app imported it, so the theme's `.p-inputswitch`
 * (3rem x 1.75rem, its own thumb geometry) landed underneath rules written to override a bare component.
 * The same switch rendered correctly in one app and wrong in the other, which is the failure mode of styling
 * by override: it is a bet that nothing else will ever set the same properties.
 *
 * There is nothing to override now. The markup and the classes are here, the visual spec is unchanged, and
 * the public API is identical — `checked`, `onCheckedChange`, `size`, the rest — so no call site moved.
 *
 * `button[role="switch"]` is the accessible primitive for this (what Radix and shadcn use): it is focusable,
 * Space/Enter toggle it for free, and `aria-checked` states it without a hidden input to keep in sync.
 */

export type SwitchProps = Omit<React.ComponentProps<"button">, "onChange" | "value" | "type"> & {
  checked?: boolean
  defaultChecked?: boolean
  onCheckedChange?: (checked: boolean) => void
  size?: "sm" | "default"
}

/** Track and thumb geometry per size. The values are the ones the old stylesheet used, unchanged. */
const TRACK = {
  default: "h-[1.15rem] w-8",
  sm: "h-3.5 w-6",
} as const

const THUMB = {
  default: "size-4 translate-x-[0.075rem]",
  sm: "size-3 translate-x-[0.0625rem]",
} as const

const THUMB_ON = {
  default: "translate-x-[0.85rem]",
  sm: "translate-x-[0.625rem]",
} as const

function Switch({
  className,
  checked,
  defaultChecked,
  onCheckedChange,
  onClick,
  id,
  size = "default",
  disabled,
  ...props
}: SwitchProps) {
  const [uncontrolledChecked, setUncontrolledChecked] = React.useState(defaultChecked ?? false)
  const isControlled = checked !== undefined
  const isOn = isControlled ? checked : uncontrolledChecked

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(event)
    if (event.defaultPrevented || disabled) return

    const next = !isOn
    if (!isControlled) setUncontrolledChecked(next)

    triggerHaptic("light")
    onCheckedChange?.(next)
  }

  return (
    <button
      type='button'
      role='switch'
      id={id}
      aria-checked={isOn}
      disabled={disabled}
      data-slot='switch'
      data-size={size}
      data-state={isOn ? "checked" : "unchecked"}
      onClick={handleClick}
      className={cn(
        "relative inline-flex shrink-0 cursor-pointer items-center rounded-full align-middle transition-colors",
        "focus-visible:ring-ring/50 focus-visible:outline-none focus-visible:ring-[3px]",
        // A neutral off-track that stays visible over cards in both themes — the reason this is not `bg-muted`.
        isOn ? "bg-primary" : "bg-muted-foreground/40",
        // Disabled must still read as a pill, so it keeps a fill and an inset ring rather than fading out.
        disabled &&
          "cursor-not-allowed opacity-65 ring-1 ring-inset ring-muted-foreground/45",
        disabled && !isOn && "bg-muted-foreground/30",
        disabled && isOn && "bg-primary/60",
        TRACK[size],
        className,
      )}
      {...props}
    >
      <span
        aria-hidden='true'
        className={cn(
          // Light thumb in BOTH states, so the control never looks disabled when it is merely off.
          "pointer-events-none absolute rounded-full bg-white shadow-[0_1px_2px_rgb(0_0_0/25%)] transition-transform",
          THUMB[size],
          isOn && THUMB_ON[size],
        )}
      />
    </button>
  )
}

export { Switch }
